import { LoadingButton } from "@mui/lab"
import { Alert, Grid2 as Grid, InputBase, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material"
import moment, { Moment } from "moment"
import { useSnackbar } from "notistack"
import React from "react"
import { DatePicker } from "../../components"
import { useAppDispatch } from "../../redux"
import { useCreateWorkLogMutation, useLazyGetWorkLogsQuery } from "../../redux/services"
import { closeBackdrop, openBackdrop } from "../../redux/slices/backdrop"
import { getWeeksInRange } from "../../utils"

interface IState { [key: string]: number }

export const WorkLog: React.FC<{ task_id: string, task_sequence_id: string, task_name: string, onSuccess: () => void }> = ({ task_id, task_name, task_sequence_id, onSuccess = () => false }) => {

    const dispatch = useAppDispatch();
    const { enqueueSnackbar } = useSnackbar();

    const [createWorkLog, createWorkLogData] = useCreateWorkLogMutation();
    const [getWorkLogs, getWorkLogData] = useLazyGetWorkLogsQuery();

    const [selectedMonth, setSelectedMonth] = React.useState<Moment>(moment());
    const [selectedYear, setSelectedYear] = React.useState<Moment>(moment());
    const [log, updateLog] = React.useState<IState>({});

    const onLogBtnClicked = async () => {
        dispatch(openBackdrop('Logging...'));
        const createWorkLogPrmArr: Promise<any>[] = Object.keys(log).map((log_date) => {
            return createWorkLog({ log_date: moment(log_date).toISOString(), hours_spent: log[log_date], task_id })
        });
        await Promise.all(createWorkLogPrmArr);
        enqueueSnackbar('Logged Successfully!', { variant: "success" });
        onSuccess();
        dispatch(closeBackdrop());
    };

    const fetchWorkLogs = async () => {
        dispatch(openBackdrop('Fetching Logs...'))
        const logResponse = await getWorkLogs({ task_id }).unwrap();

        if (logResponse.data.length) {
            let newLog: IState = {};
            logResponse.data.forEach(log => {
                const curr = moment(log.log_date).startOf('day').toString();
                newLog[curr] = log.hours_spent;
            });
            updateLog(newLog);
        };
        dispatch(closeBackdrop());
    };

    const date = selectedYear.get('year') + '/' + (selectedMonth.get('M') + 1) + "/01";
    const startDate = moment(date, 'YYYY/M/DD').startOf('month');
    const endDate = moment(date, 'YYYY/M/DD').endOf('month');
    const weeks = getWeeksInRange(startDate.toString(), endDate.toString());
    const day = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    React.useEffect(() => {
        fetchWorkLogs();
        // eslint-disable-next-line
    }, []);

    return <Grid container spacing={2}>

        <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
            {/* Year */}
            <DatePicker
                size="small"
                value={selectedYear}
                onChange={(year) => year ? setSelectedYear(year) : false}
                label="Year"
                views={['year']}
                sx={{ width: "100%" }}
            />

            {/* Month */}
            <DatePicker
                size="small"
                value={selectedMonth}
                onChange={(month) => month ? setSelectedMonth(month) : false}
                label="Month"
                views={['month']}
                sx={{ width: "100%" }}
            />
        </Stack>

        {getWorkLogData.isError && <Alert variant="outlined" severity="warning">Something went wrong, Unable to fetch logs</Alert>}

        {/* Work Log Table */}
        <Grid size={{ xs: 12 }}>
            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ background: (theme) => theme.palette.primary.light }}>
                            <TableCell>
                                <Typography variant="body2" sx={{ fontFamily: "htrts_medium" }}>
                                    Task Detail
                                </Typography>
                            </TableCell>
                            <TableCell sx={{ borderLeft: (theme) => `1px solid ${theme.palette.divider}`, padding: 0 }}>
                                <Stack>
                                    <TableCell sx={{ borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}>
                                        <Typography variant="body2" sx={{ fontFamily: "htrts_medium" }}>
                                            Week
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}>
                                        <Typography variant="body2" sx={{ fontFamily: "htrts_medium" }}>
                                            Days
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ fontFamily: "htrts_medium" }}>
                                            Task Id
                                        </Typography>
                                    </TableCell>
                                </Stack>
                            </TableCell>
                            {Array.from({ length: weeks.length }, (_, weekNoIndex) => {

                                let dates: { date: number, day: string }[] = [];

                                for (let dateAdd = 0; dateAdd < 7; dateAdd++) {
                                    let currentDate = moment(weeks[weekNoIndex].startDate).add('d', dateAdd);

                                    if (currentDate.get('M') === selectedMonth.get('M')) {
                                        dates.push({
                                            date: currentDate.get('date'),
                                            day: day[currentDate.get('d')]
                                        });
                                    }
                                }

                                return <TableCell key={weekNoIndex} align="center" sx={{ borderLeft: (theme) => `1px solid ${theme.palette.divider}`, padding: 0 }}>
                                    <Stack alignItems={"center"}>
                                        <TableCell align="center" sx={{ width: "100%", paddingX: dates.length === 1 ? '1px' : '' }}>
                                            <Typography variant={"body2"} sx={{ fontFamily: "htrts_medium", fontSize: dates.length === 1 ? '14px' : '' }}>
                                                {`Week ${weekNoIndex + 1}`}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center" sx={{ width: "100%", padding: 0 }}>
                                            {dates.map((date, dateIndex) => (
                                                <TableCell key={dateIndex} align="center" sx={{ minWidth: "58px", maxWidth: "58px", borderRight: (theme) => dateIndex === (dates.length - 1) ? `` : `1px solid ${theme.palette.divider}`, borderBottom: 0 }}>
                                                    <Typography variant="body2" sx={{ fontFamily: "htrts_medium" }}>
                                                        {date.date}
                                                    </Typography>
                                                </TableCell>
                                            ))}
                                        </TableCell>
                                        <TableCell align="center" sx={{ width: "100%", padding: 0, borderBottom: 0 }}>
                                            {dates.map((date, dateIndex) => (
                                                <TableCell key={dateIndex} align="center" sx={{ minWidth: "58px", maxWidth: "58px", borderRight: (theme) => dateIndex === (dates.length - 1) ? `` : `1px solid ${theme.palette.divider}`, borderBottom: 0 }}>
                                                    <Typography variant="body2" sx={{ fontFamily: "htrts_medium" }}>
                                                        {date.day}
                                                    </Typography>
                                                </TableCell>
                                            ))}
                                        </TableCell>
                                    </Stack>
                                </TableCell>
                            })}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                            <TableCell sx={{ minWidth: '200px', borderRight: (theme) => `1px solid ${theme.palette.divider}` }}>
                                <Typography variant="body2" color="textPrimary" sx={{ fontFamily: "htrts_light" }}>
                                    {task_name}
                                </Typography>
                            </TableCell>
                            <TableCell sx={{ minWidth: '110px', paddingX: 1, borderRight: (theme) => `1px solid ${theme.palette.divider}` }}>
                                <Typography variant="body2" color="textPrimary" sx={{ fontFamily: "htrts_light" }}>
                                    {task_sequence_id}
                                </Typography>
                            </TableCell>
                            {Array.from({ length: weeks.length }, (_, weekNoIndex) => {

                                let dates: Moment[] = [];

                                for (let dateAdd = 0; dateAdd < 7; dateAdd++) {
                                    let currentDate = moment(weeks[weekNoIndex].startDate).add('d', dateAdd);

                                    if (currentDate.get('M') === selectedMonth.get('M')) {
                                        dates.push(currentDate);
                                    }
                                }

                                return <TableCell key={weekNoIndex} align="center" sx={{ borderLeft: (theme) => `1px solid ${theme.palette.divider}`, padding: 0 }}>
                                    <Stack alignItems={"center"}>
                                        <TableCell align="center" sx={{ width: "100%", padding: 0, borderBottom: 0 }}>
                                            {dates.map((date, dateIndex) => (
                                                <TableCell key={dateIndex} align="center" sx={{ minWidth: "58px", maxWidth: "58px", borderRight: (theme) => dateIndex === (dates.length - 1) ? `` : `1px solid ${theme.palette.divider}`, borderBottom: 0 }}>
                                                    <InputBase fullWidth type="number" value={log[date.toString()]} onChange={(e) => {
                                                        updateLog({ ...log, [date.toString()]: +e.target.value })
                                                    }} />
                                                </TableCell>
                                            ))}
                                        </TableCell>
                                    </Stack>
                                </TableCell>
                            })}
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
        </Grid>

        {/* Footer Button */}
        <Grid size={{ xs: 12 }}>
            <Stack alignItems={"center"} direction={"row"} justifyContent={"center"} gap={1}>
                <LoadingButton
                    variant="outlined"
                >
                    Back
                </LoadingButton>
                <LoadingButton
                    loading={createWorkLogData.isLoading}
                    variant="contained"
                    onClick={(e) => onLogBtnClicked()}
                >
                    Done
                </LoadingButton>
            </Stack>
        </Grid>
    </Grid>
}